import {Badge, Chip, Tab, Tabs, Typography} from "@mui/material";
import { Box } from "@mui/system";
import { TabPanel } from "components/TabPanel";
import { useTabsContext } from "components/tabs/hooks";
import React, {Fragment, useEffect, useMemo} from "react";
import { useFormContext } from "react-hook-form";
import { tabFieldNames } from "../constants";
import NoteForm from "../forms/note.form";
import PreferenceForm from "../forms/preference.form";
import ProfileForm from "../forms/profile.form";
import ContractsPartial from "./contracts.partial";
import { DocumentTabPartial, getMandatoryDocuments } from "./documents.partial";
import ApplicationsPartial from "./applications.partial";

type Props = {
  tempWorker: any;
  modalMode?: boolean;
};

const TabsPartial = ({ tempWorker, modalMode }: Props) => {
  // Custom Hooks
  const { formState } = useFormContext();
  const { setBadge, tabValue, changeTab, getBadge } = useTabsContext();

  // Errors
  useEffect(() => {
    Object.entries(tabFieldNames).forEach(([tabName, fields]) => {
      setBadge(
        tabName,
        fields.some((field) => formState.errors[field] !== undefined)
          ? "error"
          : undefined
      );
    });
  }, [formState, setBadge]);

    const documentsChip = useMemo(() => {
        if(tempWorker.missing_mandatory_documents_count === null || tempWorker.missing_mandatory_documents_count === undefined){
            return '';
        }

        const expectedDocumentsCount = getMandatoryDocuments(tempWorker).length;
        if(tempWorker.missing_mandatory_documents_count <= 0){
            return <Chip label={`${expectedDocumentsCount}/${expectedDocumentsCount}`} color="success" component='span' size='small' sx={{ml: 0.5}} />;
        }

        const mandatoryDocumentCount = Math.max(expectedDocumentsCount - tempWorker.missing_mandatory_documents_count, 0);
        if(tempWorker.missing_mandatory_documents_count < expectedDocumentsCount){
            return <Chip label={`${mandatoryDocumentCount}/${expectedDocumentsCount}`} color="warning" component='span' size='small' sx={{ml: 0.5}} />
        }

        return <Chip label={`${mandatoryDocumentCount}/${expectedDocumentsCount}`} color="error" component='span' size='small' sx={{ml: 0.5}} />
    }, [tempWorker]);

  return (
    <Fragment>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          pl: "1rem",
          ...modalMode ? {
            position: "sticky",
            top: '73px',
            zIndex: 100,
            backgroundColor: "background.paper",
          } : {}
        }}
      >
        <Tabs
          value={tabValue}
          indicatorColor="secondary"
          onChange={changeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ "& button.Mui-selected": { color: "info.main" }, "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.25 } }}
          aria-label="Tab attribues candidat"
        >
          <Tab
            label={
              <Badge color={getBadge("preferences") || undefined} variant="dot">
                <Typography>
                    Préférences
                </Typography>
              </Badge>
            }
            disableRipple
            sx={{ textTransform: "none" }}
          />
          <Tab label="Missions" disableRipple sx={{ textTransform: "none" }} />
            <Tab
                label={
                    <Badge color={getBadge("applications") || undefined} variant="dot">
                        <Typography>
                            Candidatures {tempWorker.applications_count > 0 && <Chip label={tempWorker.applications_count} color="info" component='span' size='small' sx={{ml: 0.5}} />}
                        </Typography>
                    </Badge>
                }
                disableRipple
                sx={{ textTransform: "none" }}
            />
          <Tab
            label={
              <Badge color={getBadge("notes") || undefined} variant="dot">
                <Typography>
                    Notes
                </Typography>
              </Badge>
            }
            disableRipple
            sx={{ textTransform: "none" }}
          />
          <Tab
            label={
              <Badge color={getBadge("profile") || undefined} variant="dot">
                <Typography>
                    Informations
                </Typography>
              </Badge>
            }
            disableRipple
            sx={{ textTransform: "none" }}
          />
          <Tab
            label={
              <Badge color={getBadge("documents") || undefined} variant="dot">
                  <Typography>
                      Documents {documentsChip}
                  </Typography>
              </Badge>
            }
            disableRipple
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </Box>
      <Box sx={{ p: "1rem 1.5rem" }}>
        <TabPanel value={tabValue} index={0}>
          <PreferenceForm tempWorker={tempWorker} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <ContractsPartial tempWorker={tempWorker} />
        </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ApplicationsPartial tempWorker={tempWorker} />
          </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <NoteForm tempWorker={tempWorker} />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <ProfileForm />
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <DocumentTabPartial tempWorker={tempWorker} />
        </TabPanel>
      </Box>
    </Fragment>
  );
};

export { TabsPartial };
